/**
 * Mobile and Telegram WebView behaviour.
 *
 * Two problems CSS alone cannot solve:
 *
 * 1. Telegram's WebView owns a vertical-swipe gesture that drags the whole
 *    mini-app sheet down to dismiss it. A drag inside the page — which is
 *    exactly what scratching is — triggers it, so the screen slides while you
 *    scratch. `touch-action` does not reach it; only Telegram's own API does.
 *
 * 2. `100dvh` handles the address bar on real browsers, but Telegram reports
 *    its own viewport height and changes it as the sheet expands. Mirroring it
 *    into a CSS variable keeps the layout still.
 *
 * Everything here is feature-detected and no-ops outside Telegram, so the same
 * bundle serves a normal mobile browser unchanged.
 */

interface TelegramWebApp {
  ready?: () => void;
  expand?: () => void;
  disableVerticalSwipes?: () => void;
  enableClosingConfirmation?: () => void;
  setHeaderColor?: (c: string) => void;
  setBackgroundColor?: (c: string) => void;
  onEvent?: (ev: string, cb: () => void) => void;
  viewportStableHeight?: number;
  viewportHeight?: number;
  isExpanded?: boolean;
  version?: string;
  platform?: string;
  openLink?: (url: string, opts?: { try_instant_view?: boolean }) => void;
}

function tg(): TelegramWebApp | undefined {
  return (window as any).Telegram?.WebApp;
}

export function isTelegram(): boolean {
  return !!tg();
}

/** Coarse pointer / no hover — a phone or tablet rather than a desktop. */
export function isTouchDevice(): boolean {
  return window.matchMedia?.("(pointer: coarse)").matches ?? "ontouchstart" in window;
}

/**
 * Mirror the live viewport height into --app-vh.
 *
 * Telegram reports its own height and resizes the sheet; browsers move the
 * address bar. Both shift the layout under a finger that is mid-scratch.
 */
function trackViewportHeight(): void {
  const set = () => {
    const t = tg();
    const h = t?.viewportStableHeight || t?.viewportHeight || window.innerHeight;
    document.documentElement.style.setProperty("--app-vh", `${h}px`);
  };
  set();
  window.addEventListener("resize", set);
  window.visualViewport?.addEventListener("resize", set);
  tg()?.onEvent?.("viewportChanged", set);
}

export function initMobile(): void {
  trackViewportHeight();

  if (isTouchDevice()) document.body.classList.add("touch");

  const t = tg();
  if (!t) return;

  document.body.classList.add("tg");

  try {
    t.ready?.();
    /* Expand first: a collapsed sheet is the state where the drag gesture is
       most eager to dismiss. */
    t.expand?.();
    /* Bot API 7.7+. Optional-chained because older Telegram clients simply do
       not have it, and calling it blindly throws and takes the page with it. */
    t.disableVerticalSwipes?.();
    t.setHeaderColor?.("#0B0B0D");
    t.setBackgroundColor?.("#0B0B0D");
  } catch {
    /* An old client missing part of the API must not stop the game loading. */
  }
}

/**
 * Open an external URL.
 *
 * Inside Telegram a plain window.open is swallowed or opens a nested webview
 * that wallets cannot hand back from; Telegram's openLink escapes to the real
 * browser or the target app.
 */
export function openExternal(url: string): void {
  const t = tg();
  if (t?.openLink) {
    t.openLink(url, { try_instant_view: false });
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * iOS, including iPadOS.
 *
 * iPadOS reports a Mac user agent, so the touch-point count is what separates
 * an iPad from a desktop Safari — a Mac trackpad reports 0.
 */
export function isIOS(): boolean {
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
}
